import { clsx } from 'clsx'
import { forwardRef, useEffect, useId, useState } from 'react'
import { hideNumberInputArrows, inputContainer, inputStyle } from './index.css'
import { formatDisplayValue } from './utils'

type InputPrimitiveValue<GType> = GType extends 'number' | 'range'
  ? number
  : string

export interface InputPropsType<
  GType extends React.HTMLInputTypeAttribute = 'text',
> extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange'
  > {
  type?: GType
  value?: InputPrimitiveValue<GType> | undefined
  label?: React.ReactNode
  hideNumberArrows?: boolean
  isFocused?: boolean
  onChange?: (value: InputPrimitiveValue<GType> | undefined) => void
}

const InputInner = <GType extends React.HTMLInputTypeAttribute = 'text'>(
  {
    hideNumberArrows,
    type = 'text' as GType,
    label,
    value: propValue,
    onChange,
    className,
    isFocused,
    ...rest
  }: InputPropsType<GType>,
  ref: React.Ref<HTMLInputElement>,
) => {
  const genId = useId()
  const id = rest.id ?? genId

  const [displayValue, setDisplayValue] = useState(() =>
    formatDisplayValue(type, propValue),
  )

  useEffect(() => {
    setDisplayValue(formatDisplayValue(type, propValue))
  }, [propValue, type])

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const rawValue = event.target.value

    if (type === 'number' || type === 'range') {
      const normalizedValue = rawValue.replace(/^0+(\d)/, '$1')
      const parsedValue =
        normalizedValue === '' ? undefined : Number(normalizedValue)

      setDisplayValue(normalizedValue)
      onChange?.(parsedValue as InputPrimitiveValue<GType>)
    } else {
      setDisplayValue(rawValue)
      onChange?.(rawValue as InputPrimitiveValue<GType>)
    }
  }

  const inputElement = (
    <input
      id={id}
      ref={ref}
      {...rest}
      type={type}
      value={displayValue ?? ''}
      className={clsx(
        inputStyle,
        className,
        hideNumberArrows && hideNumberInputArrows,
      )}
      onChange={handleChange}
    />
  )

  if (label) {
    return (
      <div className={inputContainer}>
        <label htmlFor={id}>{label}</label>
        {inputElement}
      </div>
    )
  }

  return inputElement
}

export const Input = forwardRef(InputInner) as <
  GType extends React.HTMLInputTypeAttribute = 'text',
>(
  props: InputPropsType<GType> & { ref?: React.Ref<HTMLInputElement> },
) => ReturnType<typeof InputInner>
